import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  Building2, 
  Shield, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckSquare,
  KeyRound,
  Package,
  UserCog,
  Receipt,
  MessageSquareWarning,
  UserPlus
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

// Navigation items with permission requirements
const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: { module: 'dashboard', action: 'visualizza' } },
  { path: '/commesse', icon: Briefcase, label: 'Commesse', permission: { module: 'commesse', action: 'visualizza' } },
  { path: '/clienti', icon: Building2, label: 'Clienti', permission: { module: 'clienti', action: 'visualizza' } },
  { path: '/risorse', icon: Users, label: 'Risorse', permission: { module: 'risorse', action: 'visualizza' } },
  { path: '/ruoli', icon: Shield, label: 'Ruoli Professionali', permission: { module: 'risorse', action: 'visualizza' } },
  { path: '/timesheet', icon: Clock, label: 'Timesheet', permission: { module: 'timesheet', action: 'visualizza_proprio' } },
  { path: '/segnalazioni', icon: MessageSquareWarning, label: 'Segnalazioni', alwaysVisible: true },
];

// Admin-only items (uses role check, not permission)
const adminNavItems = [
  { path: '/gestione-utenti', icon: UserCog, label: 'Gestione Utenti', permission: { module: 'utenti', action: 'gestisci_ruoli' } },
  { path: '/gestione-inviti', icon: UserPlus, label: 'Gestione Inviti', adminOnly: true },
  { path: '/gestione-permessi', icon: Shield, label: 'Gestione Permessi', adminOnly: true },
  { path: '/ruoli-utente', icon: KeyRound, label: 'Ruoli & Permessi', adminOnly: true },
];

// PM+ items with permissions
const pmNavItems = [
  { path: '/timesheet/approvazione', icon: CheckSquare, label: 'Approvazioni Timesheet', permission: { module: 'timesheet', action: 'approva' } },
  { path: '/costi-approvazione', icon: CheckSquare, label: 'Approvazioni Costi', permission: { module: 'costi', action: 'approva' } },
  { path: '/costi-esterni', icon: Package, label: 'Costi Esterni', permission: { module: 'costi', action: 'visualizza' } },
  { path: '/ricavi', icon: Receipt, label: 'Ricavi & Fatture', permission: { module: 'ricavi', action: 'visualizza' } },
];

export const Sidebar = ({ isCollapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if item should be visible based on permission or admin status
  const isItemVisible = (item) => {
    // Items marked as alwaysVisible are shown to everyone
    if (item.alwaysVisible) return true;
    
    // Admin always sees everything
    if (user?.ruolo === 'admin') return true;
    
    // Admin-only items
    if (item.adminOnly) return false;
    
    // Permission-based visibility
    if (item.permission) {
      return hasPermission(item.permission.module, item.permission.action);
    }
    
    // Legacy role-based check (fallback)
    if (item.roles) {
      return item.roles.includes(user?.ruolo);
    }
    
    return true;
  };

  // Filter items based on permissions
  const visibleNavItems = navItems.filter(isItemVisible);
  const visibleAdminItems = adminNavItems.filter(isItemVisible);
  const visiblePmItems = pmNavItems.filter(isItemVisible);

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-slate-900 text-white z-40 transition-all duration-300 flex flex-col",
        isCollapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      <div className={cn(
        "h-16 flex items-center border-b border-white/10 px-4",
        isCollapsed ? "justify-center" : "justify-between"
      )}>
        {!isCollapsed && (
          <h1 className="text-xl font-bold tracking-tight">AGRI-GM</h1>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-white/70 hover:text-white hover:bg-white/10"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      <nav className="flex-1 py-6 px-2 space-y-1 overflow-y-auto">
        <TooltipProvider delayDuration={0}>
          {visibleNavItems.map((item) => (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200",
                    "hover:bg-white/10",
                    isActive && "bg-white/15 text-white",
                    !isActive && "text-white/70",
                    isCollapsed && "justify-center"
                  )}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                </NavLink>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                  {item.label}
                </TooltipContent>
              )}
            </Tooltip>
          ))}
          
          {/* PM/Gestione Section - shows if user has any PM permissions */}
          {visiblePmItems.length > 0 && (
            <>
              {!isCollapsed && (
                <div className="pt-4 pb-2 px-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">Gestione</p>
                </div>
              )}
              {visiblePmItems.map((item) => (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => cn(
                        "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200",
                        "hover:bg-white/10",
                        isActive && "bg-white/15 text-white",
                        !isActive && "text-white/70",
                        isCollapsed && "justify-center"
                      )}
                      data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span className="font-medium">{item.label}</span>}
                    </NavLink>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                      {item.label}
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </>
          )}

          {/* Admin Only Section */}
          {visibleAdminItems.length > 0 && (
            <>
              {!isCollapsed && (
                <div className="pt-4 pb-2 px-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">Amministrazione</p>
                </div>
              )}
              {visibleAdminItems.map((item) => (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => cn(
                        "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200",
                        "hover:bg-white/10",
                        isActive && "bg-white/15 text-white",
                        !isActive && "text-white/70",
                        isCollapsed && "justify-center"
                      )}
                      data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span className="font-medium">{item.label}</span>}
                    </NavLink>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                      {item.label}
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </>
          )}
        </TooltipProvider>
      </nav>

      <div className="p-4 border-t border-white/10">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full text-white/70 hover:text-white hover:bg-white/10",
                  isCollapsed ? "justify-center px-0" : "justify-start"
                )}
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
                {!isCollapsed && <span className="ml-3 font-medium">Esci</span>}
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                Esci
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </aside>
  );
};

export default Sidebar;
